// src/promptLibraryUtils.js - Updated with MyPrompts tag support and better search
import { v4 as uuidv4 } from 'uuid';

// Local storage key
const STORAGE_KEY = 'blue_comet_prompt_library';

/**
 * Load all saved prompts
 * @returns {Array} Array of prompt objects
 */
export const loadPrompts = () => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (!storedData) return [];
    
    const parsedData = JSON.parse(storedData);
    return Array.isArray(parsedData) ? parsedData : [];
  } catch (err) {
    console.error('Failed to load prompts:', err);
    return [];
  }
};

/**
 * Save a new prompt to the library
 * @param {string} name - Prompt name
 * @param {string} content - Prompt content
 * @param {Array} tags - Array of tags
 * @returns {Object|null} The newly created prompt or null if failed
 */
export const addPrompt = (name, content, tags = []) => {
  try {
    const trimmedName = name.trim();
    const trimmedContent = content.trim();
    
    if (!trimmedName || !trimmedContent) {
      return null;
    }
    
    // Ensure MyPrompts tag is included
    let finalTags = [...tags];
    if (!finalTags.includes('MyPrompts')) {
      finalTags = ['MyPrompts', ...finalTags];
    }
    
    const prompts = loadPrompts();
    
    // Create new prompt object
    const newPrompt = {
      id: uuidv4(),
      name: trimmedName,
      content: trimmedContent,
      tags: finalTags,
      createdAt: Date.now(),
      lastUsedAt: null,
      useCount: 0,
      source: 'user' // Add source to indicate user-created prompt
    };
    
    // Add to list and save
    prompts.push(newPrompt);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prompts));
    
    return newPrompt;
  } catch (err) {
    console.error('Failed to add prompt:', err);
    return null;
  }
};

/**
 * Update an existing prompt
 * @param {string} id - Prompt ID
 * @param {Object} updates - Object with properties to update
 * @returns {Object|null} The updated prompt or null if failed
 */
export const updatePrompt = (id, updates) => {
  try {
    const prompts = loadPrompts();
    const index = prompts.findIndex(p => p.id === id);
    
    if (index === -1) return null;
    
    // Make sure MyPrompts tag is preserved if it was there originally
    if (updates.tags && prompts[index].tags?.includes('MyPrompts') && !updates.tags.includes('MyPrompts')) {
      updates.tags = ['MyPrompts', ...updates.tags];
    }
    
    // Update the prompt
    prompts[index] = {
      ...prompts[index],
      ...updates,
      updatedAt: Date.now()
    };
    
    // Save updates
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prompts));
    
    return prompts[index];
  } catch (err) {
    console.error('Failed to update prompt:', err);
    return null;
  }
};

/**
 * Delete a prompt from the library
 * @param {string} id - Prompt ID
 * @returns {boolean} Success status
 */
export const deletePrompt = (id) => {
  try {
    const prompts = loadPrompts();
    const filteredPrompts = prompts.filter(p => p.id !== id);
    
    if (filteredPrompts.length === prompts.length) {
      return false; // No prompt was deleted
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredPrompts));
    return true;
  } catch (err) {
    console.error('Failed to delete prompt:', err);
    return false;
  }
};

/**
 * Record usage of a prompt
 * @param {string} id - Prompt ID
 * @returns {Object|null} The updated prompt or null if failed
 */
export const recordPromptUsage = (id) => {
  try {
    const prompts = loadPrompts();
    const index = prompts.findIndex(p => p.id === id);
    
    if (index === -1) return null;
    
    // Update usage stats
    prompts[index] = {
      ...prompts[index],
      lastUsedAt: Date.now(),
      useCount: (prompts[index].useCount || 0) + 1
    };
    
    // Save updates
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prompts));
    
    return prompts[index];
  } catch (err) {
    console.error('Failed to record prompt usage:', err);
    return null;
  }
};

/**
 * Generate a suggested name for a prompt based on its content
 * Uses Claude 3.5 Haiku if API is available, otherwise falls back to basic method
 * 
 * @param {string} content - The prompt content
 * @returns {Promise<string>} A suggested name for the prompt
 */
export const generatePromptName = async (content) => {
  try {
    // Import dynamically to avoid circular dependencies
    const { suggestPromptName } = await import('./promptNameSuggestions');
    
    // Use AI-powered name suggestion
    const aiSuggestedName = await suggestPromptName(content);
    
    // If we got a valid suggestion, use it
    if (aiSuggestedName && aiSuggestedName.trim() !== '') {
      return aiSuggestedName;
    }
    
    // Fall back to basic method if AI suggestion fails
    throw new Error("AI name suggestion returned empty result");
  } catch (err) {
    console.error('Error generating AI prompt name:', err);
    
    // Fallback to basic algorithm
    const words = content.trim().split(/\s+/);
    const nameWords = words.slice(0, Math.min(15, words.length));
    
    // Create a name with capitalized first letter
    let name = nameWords.join(' ');
    
    // Truncate if too long, but ensure we don't cut off mid-word
    if (name.length > 50) {
      // Find the last space before 50 chars
      const lastSpaceIndex = name.lastIndexOf(' ', 50);
      if (lastSpaceIndex > 20) { // Only truncate if we have at least 20 chars
        name = name.substring(0, lastSpaceIndex) + '...';
      } else {
        // If no space found, just truncate at 50
        name = name.substring(0, 50) + '...';
      }
    }
    
    // Capitalize first letter
    return name.charAt(0).toUpperCase() + name.slice(1);
  }
};

/**
 * Get user-created prompts (those with MyPrompts tag)
 * @returns {Array} Array of user-created prompts
 */
export const getUserPrompts = () => {
  const allPrompts = loadPrompts();
  return allPrompts.filter(prompt => 
    prompt.tags && prompt.tags.includes('MyPrompts')
  );
};

/**
 * Search prompts by text or tags
 * @param {string} query - Search query
 * @param {Array} tags - Tags to filter by (all must match)
 * @returns {Array} Filtered array of prompts
 */
export const searchPrompts = (query, tags = []) => {
  const allPrompts = loadPrompts();
  
  return allPrompts.filter(prompt => {
    // Filter by search query
    const matchesQuery = !query || 
      prompt.name.toLowerCase().includes(query.toLowerCase()) ||
      prompt.content.toLowerCase().includes(query.toLowerCase()) ||
      (prompt.tags && prompt.tags.some(tag => 
        tag.toLowerCase().includes(query.toLowerCase())
      ));
    
    // Filter by tags - prompt must have ALL specified tags
    const matchesTags = tags.length === 0 || 
      (prompt.tags && tags.every(tag => prompt.tags.includes(tag)));
    
    return matchesQuery && matchesTags;
  });
};

/**
 * Import prompts from JSON
 * @param {string} jsonData - JSON string of prompts
 * @returns {number} Number of prompts imported
 */
export const importPrompts = (jsonData) => {
  try {
    const importedPrompts = JSON.parse(jsonData);
    if (!Array.isArray(importedPrompts)) {
      throw new Error('Invalid import format');
    }
    
    const currentPrompts = loadPrompts();
    
    // Add only valid prompts with required fields
    let importCount = 0;
    importedPrompts.forEach(prompt => {
      if (prompt.name && prompt.content) {
        // Generate new ID to avoid conflicts
        const newPrompt = {
          ...prompt,
          id: uuidv4(),
          importedAt: Date.now()
        };
        
        // Make sure the MyPrompts tag is applied if importing as user prompt
        if (newPrompt.source === 'user' && (!newPrompt.tags || !newPrompt.tags.includes('MyPrompts'))) {
          newPrompt.tags = newPrompt.tags ? ['MyPrompts', ...newPrompt.tags] : ['MyPrompts'];
        }
        
        currentPrompts.push(newPrompt);
        importCount++;
      }
    });
    
    if (importCount > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(currentPrompts));
    }
    
    return importCount;
  } catch (err) {
    console.error('Failed to import prompts:', err);
    return 0;
  }
};

/**
 * Export all prompts as JSON
 * @returns {string} JSON string of all prompts
 */
export const exportPrompts = () => {
  const prompts = loadPrompts();
  return JSON.stringify(prompts, null, 2);
};

/**
 * Check if a prompt exists with the exact same content
 * @param {string} content - Prompt content
 * @returns {boolean} True if duplicate exists
 */
export const isDuplicatePrompt = (content) => {
  const prompts = loadPrompts();
  return prompts.some(p => p.content.trim() === content.trim());
};

/**
 * Initialize prompt library from CSV data
 * @param {string} csvData - CSV string containing prompt data
 * @returns {number} Number of prompts initialized
 */
export const initializeFromCsv = (csvData) => {
  try {
    // Parse CSV and convert to prompt objects
    const lines = csvData.split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    
    // Validate required CSV columns
    const nameIndex = headers.indexOf('name');
    const contentIndex = headers.indexOf('content');
    const tagsIndex = headers.indexOf('tags');
    
    if (nameIndex === -1 || contentIndex === -1) {
      console.error('CSV must have at least name and content columns');
      return 0;
    }
    
    // Convert CSV rows to prompt objects
    const currentPrompts = loadPrompts();
    let importCount = 0;
    
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      
      const values = lines[i].split(',').map(v => v.trim());
      
      // Get required fields
      const name = values[nameIndex];
      const content = values[contentIndex];
      
      // Skip invalid rows
      if (!name || !content) continue;
      
      // Process tags if available
      let tags = [];
      if (tagsIndex !== -1 && values[tagsIndex]) {
        tags = values[tagsIndex].split(';').map(t => t.trim());
      }
      
      // Ensure MyPrompts tag is included for user imports
      if (!tags.includes('MyPrompts')) {
        tags.push('MyPrompts');
      }
      
      // Create new prompt object
      const newPrompt = {
        id: uuidv4(),
        name,
        content,
        tags,
        createdAt: Date.now(),
        lastUsedAt: null,
        useCount: 0,
        source: 'csv_import'
      };
      
      // Add to list
      currentPrompts.push(newPrompt);
      importCount++;
    }
    
    // Save all prompts if any were imported
    if (importCount > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(currentPrompts));
    }
    
    return importCount;
  } catch (err) {
    console.error('Failed to initialize from CSV:', err);
    return 0;
  }
};